import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/add-restaurant — rebuilt in brand primitives. Three sections:
 * restaurant name, repeatable menu blocks, repeatable supporting documents.
 * On save, hands off to the unified menu manager with the new id.
 */

const DOCUMENT_TYPES = ['Ingredients', 'Allergens', 'Specials', 'Hours', 'Policies', 'Other'];

export default function UnifiedAddRestaurant() {
  const { userId, isInitialized } = useContext(UserContext);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [menus, setMenus] = useState([{ menu_data: '' }]);
  const [docs, setDocs] = useState([{ document_type: 'Ingredients', document_data: '' }]);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) navigate('/unified');
  }, [userId, isInitialized, navigate]);

  const save = async () => {
    if (!name.trim()) {
      setAlert({ tone: 'red', text: 'Give the restaurant a name first.' });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      const result = await dataService.createRestaurant({
        restaurant: { name: name.trim() },
        menus: menus.filter((m) => m.menu_data.trim()),
        documents: docs.filter((d) => d.document_data.trim()),
      });
      setAlert({ tone: 'green', text: 'Restaurant saved — taking you to the menu editor.' });
      setTimeout(() => navigate(`/unified/menu-manager`, { state: { restaurantId: result?.id } }), 1200);
    } catch (e) {
      setAlert({ tone: 'red', text: e?.message || 'Failed to save.' });
      setSaving(false);
    }
  };

  const completeness = (() => {
    const hasName = !!name.trim();
    const hasMenu = menus.some((m) => m.menu_data.trim());
    const hasDoc = docs.some((d) => d.document_data.trim());
    const filled = [hasName, hasMenu, hasDoc].filter(Boolean).length;
    return { filled, total: 3, percent: Math.round((filled / 3) * 100) };
  })();

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 22 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <StorefrontIcon size={12} /> NEW RESTAURANT
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            Let's <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>get you listed</em>.
          </h1>
          <p style={{ maxWidth: 520, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Start with a name and drop in your menu and supporting docs. You can keep editing afterwards.
          </p>
        </motion.section>

        {/* Progress bar */}
        <div className="cwm-sheet" style={{ padding: '14px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span className="cwm-eyebrow">SETUP PROGRESS</span>
              <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--cwm-blue-deep)' }}>{completeness.filled}/{completeness.total}</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden' }}>
              <motion.div
                initial={false}
                animate={{ width: `${completeness.percent}%` }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)', borderRadius: 999 }}
              />
            </div>
          </div>
        </div>

        {/* Alerts */}
        <AnimatePresence>
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{
                padding: '10px 14px', borderRadius: 14, marginBottom: 14,
                background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
                color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                fontSize: 13, fontWeight: 800,
              }}
            >
              {alert.text}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sections */}
        <div style={{ display: 'grid', gap: 16 }}>
          <Section eyebrow="NAME" title="What's it called?" stepNum={1} done={!!name.trim()}>
            <BrandInput value={name} onChange={setName} placeholder="e.g. Il Violino" />
          </Section>

          <Section eyebrow="MENUS" title="Drop in your menu" stepNum={2} done={menus.some((m) => m.menu_data.trim())}>
            <div style={{ display: 'grid', gap: 10 }}>
              {menus.map((menu, i) => (
                <MenuBlock
                  key={i} menu={menu} index={i}
                  canRemove={menus.length > 1}
                  onChange={(v) => setMenus(menus.map((m, idx) => idx === i ? { ...m, menu_data: v } : m))}
                  onRemove={() => setMenus(menus.filter((_, idx) => idx !== i))}
                />
              ))}
              <button type="button" onClick={() => setMenus([...menus, { menu_data: '' }])}
                      style={addBtnStyle}>
                <PlusIcon size={12} /> Add another menu
              </button>
            </div>
          </Section>

          <Section eyebrow="DOCUMENTS" title="Supporting info (optional)" stepNum={3} done={docs.some((d) => d.document_data.trim())}>
            <div style={{ display: 'grid', gap: 10 }}>
              {docs.map((doc, i) => (
                <DocBlock
                  key={i} doc={doc} index={i}
                  canRemove={docs.length > 1}
                  onTypeChange={(v) => setDocs(docs.map((d, idx) => idx === i ? { ...d, document_type: v } : d))}
                  onDataChange={(v) => setDocs(docs.map((d, idx) => idx === i ? { ...d, document_data: v } : d))}
                  onRemove={() => setDocs(docs.filter((_, idx) => idx !== i))}
                />
              ))}
              <button type="button"
                      onClick={() => setDocs([...docs, { document_type: 'Ingredients', document_data: '' }])}
                      style={addBtnStyle}>
                <PlusIcon size={12} /> Add another document
              </button>
            </div>
          </Section>

          {/* Sticky save bar */}
          <div className="cwm-sheet" style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
              You'll land in Menu Manager to refine details after save.
            </div>
            <button
              className="cwm-btn"
              onClick={save}
              disabled={saving || !name.trim()}
              style={{ opacity: (saving || !name.trim()) ? 0.6 : 1, cursor: (saving || !name.trim()) ? 'not-allowed' : 'pointer' }}
            >
              {saving ? <Spinner /> : <><SaveIcon size={14} /> Save restaurant</>}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ---------- sub-components ----------

function Section({ eyebrow, title, stepNum, done, children }) {
  return (
    <div className="cwm-sheet" style={{ padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10,
          display: 'grid', placeItems: 'center',
          background: done ? 'linear-gradient(160deg, #1E9BFF, #0F7FE8)' : 'var(--cwm-gray-100)',
          color: done ? '#fff' : 'var(--cwm-gray-500)',
          fontWeight: 900, fontSize: 14,
          boxShadow: done ? '0 6px 14px rgba(15,127,232,0.28)' : 'none',
          transition: 'all 200ms ease',
        }}>
          {done ? <CheckIcon size={16} /> : stepNum}
        </div>
        <div>
          <div className="cwm-eyebrow">{eyebrow}</div>
          <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}

function MenuBlock({ menu, index, canRemove, onChange, onRemove }) {
  return (
    <div style={{ border: '1.5px solid var(--cwm-blue-light)', borderRadius: 14, padding: 12, background: '#F7FBFF' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>MENU {index + 1}</div>
        {canRemove && (
          <button type="button" onClick={onRemove}
                  style={{ background: 'transparent', border: 'none', color: 'var(--cwm-red-text)', cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800 }}>
            <TrashIcon size={12} /> Remove
          </button>
        )}
      </div>
      <textarea
        value={menu.menu_data}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder={"Appetizers\n— Burrata & heirloom tomatoes, $16\n— Garlic bread, $8\n\nPasta\n— Gluten-free pappardelle, $28\n— Vegan mushroom risotto, $24"}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '12px 14px',
          borderRadius: 12,
          border: '1px solid rgba(15,127,232,0.12)',
          background: '#fff',
          color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 500, lineHeight: 1.55,
          outline: 'none', resize: 'vertical',
          minHeight: 110,
        }}
      />
    </div>
  );
}

function DocBlock({ doc, index, canRemove, onTypeChange, onDataChange, onRemove }) {
  return (
    <div style={{ border: '1.5px solid var(--cwm-blue-light)', borderRadius: 14, padding: 12, background: '#F7FBFF' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center', gap: 10 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>DOCUMENT {index + 1}</div>
        {canRemove && (
          <button type="button" onClick={onRemove}
                  style={{ background: 'transparent', border: 'none', color: 'var(--cwm-red-text)', cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800 }}>
            <TrashIcon size={12} /> Remove
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {DOCUMENT_TYPES.map((type) => {
          const on = doc.document_type === type;
          return (
            <button key={type} type="button" onClick={() => onTypeChange(type)}
                    style={{
                      padding: '5px 12px', borderRadius: 999,
                      background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                      color: on ? '#fff' : 'var(--cwm-blue-deep)',
                      border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 900,
                      cursor: 'pointer',
                      boxShadow: on ? '0 4px 10px rgba(15,127,232,0.28)' : 'none',
                    }}>
              {type}
            </button>
          );
        })}
      </div>
      <textarea
        value={doc.document_data}
        onChange={(e) => onDataChange(e.target.value)}
        rows={3}
        placeholder={`Paste ${doc.document_type.toLowerCase()} info here…`}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '12px 14px',
          borderRadius: 12,
          border: '1px solid rgba(15,127,232,0.12)',
          background: '#fff',
          color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 500, lineHeight: 1.5,
          outline: 'none', resize: 'vertical',
          minHeight: 72,
        }}
      />
    </div>
  );
}

function BrandInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '14px 18px',
        borderRadius: 14,
        border: '1.5px solid var(--cwm-blue-light)',
        background: '#F7FBFF',
        color: 'var(--cwm-ink)',
        fontFamily: 'var(--cwm-font)', fontSize: 16, fontWeight: 700,
        outline: 'none',
      }}
    />
  );
}

function Spinner() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: 999, background: '#fff',
          animation: 'cwmDot 900ms ease-in-out infinite',
          animationDelay: `${i * 150}ms`,
        }}/>
      ))}
    </span>
  );
}

const addBtnStyle = {
  padding: '10px 16px',
  borderRadius: 14,
  background: 'transparent',
  color: 'var(--cwm-blue-deep)',
  border: '1.5px dashed var(--cwm-blue-light)',
  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  justifyContent: 'center',
};

// ---------- icons ----------
function StorefrontIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10l1-6h16l1 6M3 10v10h18V10M3 10h18"/></svg>; }
function CheckIcon({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function TrashIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function SaveIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8"/></svg>; }
